import { useMemo } from 'react';
import { Path, UseFormReturn, useWatch } from 'react-hook-form';
import { FormDisplayMode } from '@models/Forms/IForms';
import { IFieldElem } from '@models/IFormData';

const NO_NAMES: never[] = [];

/** Формат справочника/АК может ссылаться на соседние поля, меняемые каскадом. */
export const useDisplayModeFields = <T extends object>(
    mode: FormDisplayMode | undefined,
    fields: Record<string, IFieldElem>,
    methods: UseFormReturn<T>,
) => {
    const names = useMemo(
        () =>
            Object.values(fields)
                .filter((field) => field.index >= 0)
                .map((field) => field.name)
                .filter(Boolean) as Path<T>[],
        [fields],
    );
    // Вне режима view — без имён: выключенный useWatch всё равно подписан на значения, и подписка на все поля
    // заставляла RHF клонировать значения всей формы на каждый ввод в документе.
    const watching = mode === 'view';
    const values = useWatch({ control: methods.control, name: watching ? names : NO_NAMES, disabled: !watching });
    return useMemo(() => {
        if (mode !== 'view') return fields;
        return Object.fromEntries(
            Object.entries(fields).map(([key, field]) => [
                key,
                field.name && field.index >= 0 ? { ...field, value: methods.getValues(field.name as Path<T>) } : field,
            ]),
        );
        // useWatch инвалидирует снимок; getValues учитывает записи до установки подписки.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, fields, methods, values]);
};
